import { effect, inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import type { Session, SessionOptions } from 'diffusion';
import { Observable, ReplaySubject, Subject, Subscription } from 'rxjs';
import { delay } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthStore } from '../stores/auth.store';

@Injectable()
export abstract class AbstractDiffusionSessionService {
  public abstract diffusionSession$: Observable<Session | null>;
}

@Injectable({ providedIn: 'root' })
export class DiffusionSessionService extends AbstractDiffusionSessionService implements OnDestroy {
  private session: Session | null = null;
  private readonly diffusionSessionSubject$ = new ReplaySubject<Session | null>(1);
  public diffusionSession$ = this.diffusionSessionSubject$.asObservable();

  private readonly subscriptions: Subscription[] = [];
  private readonly sessionRecreationTrigger$ = new Subject<void>();

  readonly #auth = inject(AuthStore);
  readonly #zone = inject(NgZone);

  private reconnectAttempts = 0;
  private failedConnectAttempts = 0;
  private readonly maxFailedConnectAttempts = 5;
  private isConnecting = false;

  constructor() {
    super();

    effect(() => {
      const token = this.#auth.accessToken();
      const username = this.#auth.username();

      if (token && username) {
        this.#zone.runOutsideAngular(() => {
          this.#connect(token, username).catch((err) =>
            console.error('[Diffusion] #connect threw:', err),
          );
        });
      } else if (this.session || this.isConnecting) {
        // Only disconnect when a session or in-progress connection actually exists.
        // AuthStore is populated field-by-field; without this guard, the intermediate
        // firings (e.g. token set but accessToken/username still null) would call
        // disconnect() spuriously and tear down a connection that is about to be made.
        this.disconnect();
      }
    });

    this.subscriptions.push(
      this.sessionRecreationTrigger$.pipe(delay(10000)).subscribe(() => {
        const token = this.#auth.accessToken();
        const username = this.#auth.username();
        if (token && username) {
          this.#zone.runOutsideAngular(() => {
            this.#connect(token, username).catch((err) =>
              console.error('[Diffusion] #connect threw:', err),
            );
          });
        }
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  // CONNECT
  async #connect(
    authToken: string,
    username: string,
    host = environment.push_url,
    isFallback = false,
  ) {
    if (this.session?.isConnected() || this.isConnecting) {
      return;
    }
    this.isConnecting = true;

    let connectFn: ((options: SessionOptions) => Promise<Session>) | undefined;
    try {
      await import('./diffusion-polyfills');
      const mod = await import('diffusion');
      // ng serve exposes named exports (mod.connect); ng build wraps CJS as `export default`
      // so mod.connect is undefined and the real function is on mod.default.connect.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const fn = mod.connect ?? (mod as any).default?.connect;
      if (typeof fn !== 'function') {
        throw new Error(
          'diffusion.connect export not found — module may not have loaded correctly',
        );
      }
      connectFn = fn as (options: SessionOptions) => Promise<Session>;
    } catch (err) {
      this.isConnecting = false;
      console.error('[Diffusion] Failed to load diffusion dependencies:', err);
      this.#handleFailedConnection();
      return;
    }

    // Guard against disconnect() being called while the dynamic imports were suspended.
    if (!this.isConnecting || !connectFn) return;

    const maximumTimeoutDuration = 1000 * 60 * 10;
    const maximumAttemptInterval = 1000 * 10;
    const maximumAttempts = 10;

    const reconnectionStrategy = (start: () => void, abort: () => void) => {
      if (this.reconnectAttempts > maximumAttempts) {
        abort();
      } else {
        const wait = Math.min(Math.pow(2, this.reconnectAttempts++) * 100, maximumAttemptInterval);
        setTimeout(start, wait);
      }
    };

    const options: SessionOptions = {
      host,
      port: environment.push_port,
      principal: username,
      credentials: authToken,
      transports: ['WS', 'XHR', 'WS'],
      reconnect: {
        timeout: maximumTimeoutDuration,
        strategy: reconnectionStrategy,
      },
    };

    const sessionPromise = connectFn(options);

    sessionPromise.then(
      (session: Session) => {
        this.isConnecting = false;
        this.session = session;
        this.reconnectAttempts = 0;
        this.failedConnectAttempts = 0;

        localStorage.setItem('diffusionSession', 'connected');

        session.on('close', () => {
          this.diffusionSessionSubject$.next(null);
          this.sessionRecreationTrigger$.next();
        });

        session.on('disconnect', (reason) => {
          if (!reason.canReconnect) {
            this.diffusionSessionSubject$.next(null);
            this.sessionRecreationTrigger$.next();
          }
        });

        session.on('error', (err) => {
          console.error('[Diffusion] Session error:', err);
        });

        session.on('reconnect', () => {
          this.reconnectAttempts = 0;
        });

        this.diffusionSessionSubject$.next(session);
      },
      (err) => {
        this.isConnecting = false;
        console.error('[Diffusion] Connection failed:', err?.message ?? err);
        const isAuthFailure =
          typeof err?.message === 'string' &&
          err.message.toLowerCase().includes('authentication failed');

        if (isAuthFailure && environment.push_url_fallback && !isFallback) {
          console.log('[Diffusion] Auth failed, retrying with fallback host');
          void this.#connect(authToken, username, environment.push_url_fallback, true);
          return;
        }

        this.#handleFailedConnection();
      },
    );
  }

  #handleFailedConnection() {
    if (this.session && !this.session.isClosed()) {
      this.session.close();
    }

    this.session = null;
    this.diffusionSessionSubject$.next(null);
    this.failedConnectAttempts++;

    if (this.failedConnectAttempts <= this.maxFailedConnectAttempts) {
      this.sessionRecreationTrigger$.next();
    }
  }

  // DISCONNECT
  disconnect() {
    this.isConnecting = false;
    if (this.session && !this.session.isClosed()) {
      this.session.close();
    }
    this.session = null;
    this.diffusionSessionSubject$.next(null);
  }
}
